var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import PropTypes from 'prop-types';
import { Transition } from 'react-transition-group';
import cx from 'classnames';
import styles from './index.module.scss';
var duration = 700;
var getVisibilityByBreakpoint = function (alwaysOpen) {
    return Object.entries(alwaysOpen).reduce(function (currentValue, _a) {
        var breakpoint = _a[0], visible = _a[1];
        var styleName = styles["hidden-".concat(breakpoint)];
        if (!currentValue)
            return visible ? currentValue : styleName;
        return visible ? currentValue : "".concat(currentValue, " ").concat(styleName);
    }, undefined);
};
var getMaxHeight = function (state, height, collapsedHeight, isOpen) {
    if (state === 'exited') {
        if (isOpen)
            return "".concat(height, "px");
        return collapsedHeight ? "".concat(collapsedHeight, "px") : undefined;
    }
    if (state === 'exiting') {
        if (isOpen)
            return "".concat(height, "px");
        return collapsedHeight ? "".concat(collapsedHeight, "px") : '0px';
    }
    if (state === 'entered')
        return isOpen ? "none" : "".concat(height, "px");
    return "".concat(height, "px");
};
var getVisibility = function (state, isOpen, collapsedHeight, breakpointVisibility) {
    if (state === 'exited')
        return isOpen || !!collapsedHeight ? null : breakpointVisibility;
    if (state === 'exiting')
        return isOpen ? null : null;
    if (state === 'entered')
        return isOpen ? null : null;
    return null;
};
/**
 * @tdr
 */
var ExpansionPanel = forwardRef(function (_a, ref) {
    var _b, _c;
    var isOpen = _a.isOpen, alwaysOpen = _a.alwaysOpen, collapsedHeight = _a.collapsedHeight, className = _a.className, props = __rest(_a, ["isOpen", "alwaysOpen", "collapsedHeight", "className"]);
    var content = useRef(null);
    useImperativeHandle(ref, function () { return content.current; }, []);
    var breakpointVisibility = alwaysOpen ? getVisibilityByBreakpoint(alwaysOpen) : styles.hidden;
    var marginTop = isOpen ? (_b = content === null || content === void 0 ? void 0 : content.current) === null || _b === void 0 ? void 0 : _b.marginTop : 0;
    var marginBottom = isOpen ? (_c = content === null || content === void 0 ? void 0 : content.current) === null || _c === void 0 ? void 0 : _c.marginBottom : 0;
    return (React.createElement(Transition, { in: isOpen, timeout: duration }, function (state) {
        var _a;
        var maxHeight = getMaxHeight(state, (_a = content === null || content === void 0 ? void 0 : content.current) === null || _a === void 0 ? void 0 : _a.scrollHeight, collapsedHeight, isOpen);
        var visibility = getVisibility(state, isOpen, collapsedHeight, breakpointVisibility);
        var classNames = cx(styles.panelBody, visibility || styles.inheritDisplay, state, className);
        return (React.createElement("div", __assign({ ref: content, style: {
                maxHeight: maxHeight,
                marginTop: marginTop,
                marginBottom: marginBottom,
            }, className: classNames }, props)));
    }));
});
ExpansionPanel.propTypes = {
    // ----------------------------- Warning --------------------------------
    // | These PropTypes are generated from the TypeScript type definitions |
    // |     To update them edit the d.ts file and run "yarn proptypes"     |
    // ----------------------------------------------------------------------
    alwaysOpen: PropTypes.shape({
        lg: PropTypes.bool.isRequired,
        md: PropTypes.bool.isRequired,
        sm: PropTypes.bool.isRequired,
        xl: PropTypes.bool.isRequired,
        xs: PropTypes.bool.isRequired,
    }),
    className: PropTypes.string,
    collapsedHeight: PropTypes.number,
    isOpen: PropTypes.bool.isRequired,
};
ExpansionPanel.defaultProps = {
    alwaysOpen: undefined,
    collapsedHeight: undefined,
    className: undefined,
};
/**
 * @tdr
 * @deprecated since 2022-07-04 when it was moved into Accordion
 * @tdr-deprecated-date: 2025-01-20
 */
export default ExpansionPanel;
//# sourceMappingURL=index.js.map